import { useEffect } from "react"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { IconMapPin } from "@tabler/icons-react"
import { toast } from "sonner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { EditableCardActions, Field, FieldGrid } from "./field"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { applicationDetailsDraft, applicationDetailsRequest } from "@/lib/application-details"
import { api } from "@/lib/api"
import { fmtDate, formatAddress } from "@/lib/benefit-format"
import { saveBenefitChange } from "@/lib/benefit-save"
import type { AddressDto, BenefitDetailDto } from "@/lib/benefit-types"

export function ApplicationDetailsSection({ benefit, canEdit, onUpdated }: {
  benefit: BenefitDetailDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
}) {
  const [editing, setEditing, token] = useBenefitEdit(benefit.concurrencyToken, "addresses")
  const [pending, setSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const saving = pending || reviewing
  const [draft, setDraft] = useBenefitDraftState("addresses", "draft", () => applicationDetailsDraft(benefit))
  useEffect(() => { if (!editing) setDraft(applicationDetailsDraft(benefit)) }, [benefit, editing])

  const save = async () => {
    if (!editing || saving || !canEdit) return
    let request: ReturnType<typeof applicationDetailsRequest>
    try { request = applicationDetailsRequest(draft, token) }
    catch (error) { toast.error(error instanceof Error ? error.message : "Check the application details."); return }
    setSaving(true)
    const result = await saveBenefitChange(benefit.id, onUpdated,
      () => api.patch<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}/application-details`, request),
      "Application date and addresses updated.")
    setSaving(false)
    if (result.saved || result.reloaded) setEditing(false)
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconMapPin className="size-5" />
            <div><CardTitle className="text-base">Application Details</CardTitle>
              <CardDescription>Business application date and independent household addresses</CardDescription></div>
          </div>
          <EditableCardActions isEditing={editing} isSaving={saving} canEdit={canEdit} saveDisabled={!canEdit} restorePanelFocus
            onEdit={() => { if (!canEdit) return; setDraft(applicationDetailsDraft(benefit)); setEditing(true) }}
            onCancel={() => setEditing(false)} onSave={save} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing ? <>
          <div className="max-w-xs space-y-1.5">
            <Label htmlFor="intake-application-date">Application Date</Label>
            <Input id="intake-application-date" type="date" value={draft.applicationDate} disabled={saving}
              onChange={event => setDraft(current => ({ ...current, applicationDate: event.target.value }))} />
            <p className="text-xs text-muted-foreground">The intake audit timestamp does not change.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {(["residential", "mailing"] as const).map(kind => (
              <fieldset key={kind} className="space-y-3">
                <legend className="mb-2 text-sm font-medium capitalize">{kind} Address</legend>
                {([["address", "Street Address", 50], ["city", "City", 30], ["state", "State", 2], ["zipCode", "ZIP Code", 10]] as const).map(([field, label, max]) => (
                  <div key={field} className="space-y-1.5">
                    <Label htmlFor={`${kind}-${field}`}>{label}</Label>
                    <Input id={`${kind}-${field}`} aria-label={`${kind} ${label}`} maxLength={max}
                      value={draft[kind][field as keyof AddressDto]} disabled={saving}
                      onChange={event => setDraft(current => ({ ...current, [kind]: { ...current[kind], [field]: event.target.value } }))} />
                  </div>
                ))}
              </fieldset>
            ))}
          </div>
          <Button variant="outline" size="sm" disabled={saving}
            onClick={() => setDraft(current => ({ ...current, mailing: { ...current.residential } }))}>
            Copy residential to mailing
          </Button>
        </> : <FieldGrid cols={3}>
          <Field label="Application Date" value={fmtDate(benefit.applicationDate)} />
          <Field label="Residential Address" value={formatAddress(benefit.addresses?.Residential)} />
          <Field label="Mailing Address" value={formatAddress(benefit.addresses?.Mailing)} />
        </FieldGrid>}
      </CardContent>
    </Card>
  )
}
